/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Tiptap Rich Text Core Editor Component (Task 10.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';

import { lowlight } from './extensions/lowlight-config';
import { SlashCommandsExtension } from './slash-command/slash-command-extension';
import { FloatingAIBubbleMenu } from './ai-menu/FloatingAIBubbleMenu';
import { useEditorStore } from '../../stores/useEditorStore';

export interface TiptapEditorProps {
  onEditorReady?: (editor: ReturnType<typeof useEditor>) => void;
}

export const TiptapEditor: React.FC<TiptapEditorProps> = ({ onEditorReady }) => {
  const { contentHtml, setContent, setStatus, saveDraft, loadDraft } = useEditorStore();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-primary underline font-medium hover:text-primary-hover transition-colors',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full my-4 border border-surface-border',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-surface-border bg-muted/60 p-2 font-bold text-left text-xs',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-surface-border p-2 text-xs',
        },
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: 'list-none pl-0 my-3 space-y-1',
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'flex items-start gap-2 text-sm',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'rounded-xl max-w-full h-auto my-4 shadow-md border border-surface-border',
        },
      }),
      SlashCommandsExtension,
    ],
    content: contentHtml,
    editorProps: {
      attributes: {
        class:
          'tiptap prose prose-neutral dark:prose-invert max-w-none focus:outline-none min-h-[420px] px-4 py-3 sm:px-6 sm:py-5 leading-relaxed text-foreground',
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const src = e.target?.result as string;
              if (src && editor) {
                editor.chain().focus().setImage({ src }).run();
              }
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: activeEditor }) => {
      const html = activeEditor.getHTML();
      const json = activeEditor.getJSON() as Record<string, unknown>;
      setContent(html, json);

      // Debounce auto-save to local draft (2000ms)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setStatus('saving');
        setTimeout(() => {
          saveDraft();
        }, 300);
      }, 2000);
    },
  });

  // Notify parent component when editor instance is ready
  useEffect(() => {
    if (editor) {
      onEditorReady?.(editor);
    }
  }, [editor, onEditorReady]);

  // Load existing draft if available on mount
  useEffect(() => {
    const loaded = loadDraft();
    if (loaded && editor) {
      const currentStoredHtml = useEditorStore.getState().contentHtml;
      if (editor.getHTML() !== currentStoredHtml) {
        editor.commands.setContent(currentStoredHtml);
      }
    }
  }, [editor, loadDraft]);

  // Cleanup debouncing timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="relative rounded-2xl border border-surface-border bg-surface shadow-glass overflow-hidden transition-colors">
      <FloatingAIBubbleMenu editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
